import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TextField, Select, MenuItem, FormControl, Tooltip, Divider,
} from "@mui/material";

const OTROS_DEFAULT = {
  accidentesTrabajo: null,
  accidentesParteCuerpo: "",
  accidentesFecha: "",
  accidentesSecuelas: "",
  enfermedadOcupacional: null,
  certificadaInpsasel: null,
  cambiaTrabajo: null,
};

const TEMPLATE = [
  // Físicos
  { id: "ruido",          categoria: "Físicos",        agente: "Ruido" },
  { id: "calor",          categoria: "Físicos",        agente: "Calor / Temperatura °C" },
  { id: "frio",           categoria: "Físicos",        agente: "Frío / Temperatura °C" },
  { id: "vibraciones",    categoria: "Físicos",        agente: "Vibraciones" },
  { id: "rad_ion",        categoria: "Físicos",        agente: "Radiaciones ionizantes" },
  { id: "rad_no_ion",     categoria: "Físicos",        agente: "Radiaciones no ionizantes" },
  // Químicos
  { id: "humos",          categoria: "Químicos",       agente: "Humos" },
  { id: "polvos",         categoria: "Químicos",       agente: "Polvos y/o fibras Orgánicas o Inorgánicas" },
  { id: "metales",        categoria: "Químicos",       agente: "Metales pesados y Metaloides" },
  { id: "gases",          categoria: "Químicos",       agente: "Gases y Vapores" },
  { id: "solventes",      categoria: "Químicos",       agente: "Solventes" },
  // Biológicos
  { id: "bio_organico",   categoria: "Biológicos",     agente: "Orgánicos — Exp. a animales/insectos" },
  { id: "bio_inorganico", categoria: "Biológicos",     agente: "Inorgánicos — Contacto con desechos" },
  // Disergonómico
  { id: "sedestacion",    categoria: "Disergonómico",  agente: "Posiciones / Sedestación prolongada" },
  { id: "bipedest",       categoria: "Disergonómico",  agente: "Bipedestación prolongada" },
  { id: "rodillas",       categoria: "Disergonómico",  agente: "De rodillas" },
  { id: "cuclillas",      categoria: "Disergonómico",  agente: "En cuclillas" },
  // Mecánicos
  { id: "mec_altura",     categoria: "Mecánicos",      agente: "Trabajo en altura" },
  { id: "mec_guarda",     categoria: "Mecánicos",      agente: "Equipos sin guarda" },
  { id: "mec_otros",      categoria: "Mecánicos",      agente: "Otros" },
];

const EXP = ["Exp. Aguda", "Exp. Crónica", "Exp. Aguda y Crónica"];
const SI_NO = ["Sí", "No"];

const TIPO_OPTIONS = {
  ruido:          EXP,
  calor:          EXP,
  frio:           EXP,
  vibraciones:    ["Cuerpo entero", "Segmentaria", ...EXP],
  rad_ion:        EXP,
  rad_no_ion:     EXP,
  humos:          EXP,
  polvos:         EXP,
  metales:        EXP,
  gases:          EXP,
  solventes:      EXP,
  bio_organico:   SI_NO,
  bio_inorganico: SI_NO,
  sedestacion:    ["Características Silla", "Ocasional", "Frecuente", "Permanente"],
  bipedest:       ["Ocasional", "Frecuente", "Permanente"],
  rodillas:       ["Ocasional", "Frecuente", "Permanente"],
  cuclillas:      ["Ocasional", "Frecuente", "Permanente"],
  mec_altura:     SI_NO,
  mec_guarda:     SI_NO,
  mec_otros:      SI_NO,
};

const parsePayload = (saved) => {
  if (!saved) return { rows: [], otros: { ...OTROS_DEFAULT } };
  let parsed = saved;
  if (typeof saved === "string") {
    try { parsed = JSON.parse(saved); } catch { return { rows: [], otros: { ...OTROS_DEFAULT } }; }
  }
  if (Array.isArray(parsed)) return { rows: parsed, otros: { ...OTROS_DEFAULT } };
  return {
    rows: Array.isArray(parsed.rows) ? parsed.rows : [],
    otros: { ...OTROS_DEFAULT, ...(parsed.otros || {}) },
  };
};

const buildRows = (saved) => {
  const { rows: list } = parsePayload(saved);
  return TEMPLATE.map((t) => {
    const existing = list.find((r) => r.id === t.id);
    return { ...t, tipo: "", descripcion: "", meses: "", anos: "", ...existing };
  });
};

const buildOtros = (saved) => parsePayload(saved).otros;

const rowHasData = (r) => r.tipo || r.descripcion || r.meses || r.anos;
const hasData = (rows) => rows.some(rowHasData);
const hasOtrosData = (otros) => Object.values(otros).some((v) => v !== null && v !== "");

const SiNoSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 72 }}>
    <Select value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} displayEmpty variant="standard">
      <MenuItem value=""><em>—</em></MenuItem>
      <MenuItem value="si">Sí</MenuItem>
      <MenuItem value="no">No</MenuItem>
    </Select>
  </FormControl>
);

const OtrosRow = ({ label, value, onChange, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 300 }}>
      <Typography variant="body2">{label}</Typography>
      <SiNoSelect value={value} onChange={onChange} />
    </Box>
    {children}
  </Box>
);

const AntecedentesOcupacionales = forwardRef(({ evaluation, editing }, ref) => {
  const [editRows, setEditRows] = useState(() => buildRows(evaluation?.antecedentesOcupacionales));
  const [editOtros, setEditOtros] = useState(() => buildOtros(evaluation?.antecedentesOcupacionales));

  useEffect(() => {
    if (editing) {
      setEditRows(buildRows(evaluation?.antecedentesOcupacionales));
      setEditOtros(buildOtros(evaluation?.antecedentesOcupacionales));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useImperativeHandle(ref, () => ({
    getPayload: () => ({
      rows: editRows.filter(rowHasData),
      otros: editOtros,
    }),
  }));

  const updateRow = (id, field, value) =>
    setEditRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));

  const setOtros = (field, value) =>
    setEditOtros((prev) => ({ ...prev, [field]: value }));

  const categories = [...new Set(TEMPLATE.map((t) => t.categoria))];

  const viewRows = buildRows(evaluation?.antecedentesOcupacionales);
  const viewOtros = buildOtros(evaluation?.antecedentesOcupacionales);
  const filled = viewRows.filter(rowHasData);

  return (
    <Box>
      {editing ? (
        <>
          {categories.map((cat) => (
            <Box key={cat} sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
                {cat}
              </Typography>
              <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 0.75 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "28%" }}>Agente</TableCell>
                    <TableCell sx={{ width: "22%" }}>Tipo / Características</TableCell>
                    <TableCell sx={{ width: "30%" }}>Breve descripción</TableCell>
                    <TableCell sx={{ width: "10%" }} align="center">Meses</TableCell>
                    <TableCell sx={{ width: "10%" }} align="center">Años</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editRows.filter((r) => r.categoria === cat).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography variant="body2">{row.agente}</Typography>
                      </TableCell>
                      <TableCell>
                        {TIPO_OPTIONS[row.id] ? (
                          <FormControl fullWidth variant="standard" size="small">
                            <Select value={row.tipo} onChange={(e) => updateRow(row.id, "tipo", e.target.value)} displayEmpty>
                              <MenuItem value=""><em>—</em></MenuItem>
                              {TIPO_OPTIONS[row.id].map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            size="small" fullWidth variant="standard"
                            value={row.tipo}
                            onChange={(e) => updateRow(row.id, "tipo", e.target.value)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" fullWidth variant="standard"
                          value={row.descripcion}
                          onChange={(e) => updateRow(row.id, "descripcion", e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" fullWidth variant="standard" type="number"
                          value={row.meses}
                          onChange={(e) => updateRow(row.id, "meses", e.target.value)}
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" fullWidth variant="standard" type="number"
                          value={row.anos}
                          onChange={(e) => updateRow(row.id, "anos", e.target.value)}
                          inputProps={{ style: { textAlign: "center" } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}

          {/* ── Otros ── */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 2, display: "block" }}>
            Otros
          </Typography>

          <OtrosRow
            label="¿Ha sufrido accidentes de trabajo?"
            value={editOtros.accidentesTrabajo}
            onChange={(v) => setOtros("accidentesTrabajo", v)}
          >
            {editOtros.accidentesTrabajo === "si" && (
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", flex: 1 }}>
                <TextField
                  size="small" variant="standard" label="¿Qué parte del cuerpo?" sx={{ flex: 1, minWidth: 160 }}
                  value={editOtros.accidentesParteCuerpo}
                  onChange={(e) => setOtros("accidentesParteCuerpo", e.target.value)}
                />
                <TextField
                  size="small" variant="standard" label="Fecha del accidente" sx={{ width: 150 }}
                  value={editOtros.accidentesFecha}
                  onChange={(e) => setOtros("accidentesFecha", e.target.value)}
                  placeholder="DD/MM/AAAA"
                />
                <TextField
                  size="small" variant="standard" label="¿Dejó secuelas?" sx={{ flex: 1, minWidth: 160 }}
                  value={editOtros.accidentesSecuelas}
                  onChange={(e) => setOtros("accidentesSecuelas", e.target.value)}
                />
              </Box>
            )}
          </OtrosRow>

          <OtrosRow
            label="¿Ha padecido o padece de enfermedad ocupacional?"
            value={editOtros.enfermedadOcupacional}
            onChange={(v) => setOtros("enfermedadOcupacional", v)}
          />

          <OtrosRow
            label="¿Fue certificada por el INPSASEL?"
            value={editOtros.certificadaInpsasel}
            onChange={(v) => setOtros("certificadaInpsasel", v)}
          />

          <OtrosRow
            label="¿Cambia de trabajo con frecuencia?"
            value={editOtros.cambiaTrabajo}
            onChange={(v) => setOtros("cambiaTrabajo", v)}
          />
        </>
      ) : !hasData(viewRows) && !hasOtrosData(viewOtros) ? (
        <Typography variant="body2" color="text.secondary">Sin antecedentes registrados.</Typography>
      ) : (
        <>
          {categories.map((cat) => {
            const catFilled = filled.filter((r) => r.categoria === cat);
            if (catFilled.length === 0) return null;
            return (
              <Box key={cat} sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {cat}
                </Typography>
                <Table size="small" sx={{ "& td, & th": { py: 0.5, px: 0.75 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "28%" }}>Agente</TableCell>
                      <TableCell sx={{ width: "22%" }}>Tipo</TableCell>
                      <TableCell sx={{ width: "30%" }}>Descripción</TableCell>
                      <TableCell sx={{ width: "10%" }} align="center">Meses</TableCell>
                      <TableCell sx={{ width: "10%" }} align="center">Años</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {catFilled.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell><Typography variant="body2">{row.agente}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{row.tipo || "—"}</Typography></TableCell>
                        <TableCell>
                          <Tooltip title={row.descripcion || ""} placement="top" disableHoverListener={!row.descripcion}>
                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180, cursor: row.descripcion ? "help" : "default" }}>
                              {row.descripcion || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center"><Typography variant="body2">{row.meses || "—"}</Typography></TableCell>
                        <TableCell align="center"><Typography variant="body2">{row.anos || "—"}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          })}

          {hasOtrosData(viewOtros) && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
                Otros
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {viewOtros.accidentesTrabajo !== null && (
                  <Box>
                    <Typography variant="body2">
                      ¿Ha sufrido accidentes de trabajo? <strong>{viewOtros.accidentesTrabajo === "si" ? "Sí" : "No"}</strong>
                    </Typography>
                    {viewOtros.accidentesTrabajo === "si" && (
                      <Box sx={{ pl: 2, mt: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
                        {viewOtros.accidentesParteCuerpo && <Typography variant="body2" color="text.secondary">Parte del cuerpo: {viewOtros.accidentesParteCuerpo}</Typography>}
                        {viewOtros.accidentesFecha && <Typography variant="body2" color="text.secondary">Fecha: {viewOtros.accidentesFecha}</Typography>}
                        {viewOtros.accidentesSecuelas && <Typography variant="body2" color="text.secondary">Secuelas: {viewOtros.accidentesSecuelas}</Typography>}
                      </Box>
                    )}
                  </Box>
                )}
                {viewOtros.enfermedadOcupacional !== null && (
                  <Typography variant="body2">
                    ¿Enfermedad ocupacional? <strong>{viewOtros.enfermedadOcupacional === "si" ? "Sí" : "No"}</strong>
                  </Typography>
                )}
                {viewOtros.certificadaInpsasel !== null && (
                  <Typography variant="body2">
                    ¿Certificada por INPSASEL? <strong>{viewOtros.certificadaInpsasel === "si" ? "Sí" : "No"}</strong>
                  </Typography>
                )}
                {viewOtros.cambiaTrabajo !== null && (
                  <Typography variant="body2">
                    ¿Cambia de trabajo con frecuencia? <strong>{viewOtros.cambiaTrabajo === "si" ? "Sí" : "No"}</strong>
                  </Typography>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
});

export default AntecedentesOcupacionales;
